import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import {
  FiCalendar,
  FiMapPin,
  FiUsers,
  FiBarChart,
  FiGift,
  FiFileText,
  FiUser,
  FiMenu,
  FiLogOut,
  FiEye,
  FiLayers
} from './Icons';

const SidebarContainer = styled.aside`
  width: 280px;
  min-height: 100vh;
  background: linear-gradient(180deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.accent} 100%);
  color: ${props => props.theme.colors.text.inverse};
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.theme.shadows.medium};

  @media (max-width: 1024px) {
    width: 260px;
  }

  @media (max-width: 768px) {
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    width: 280px;
  }
`;

const SidebarHeader = styled.div`
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  background: rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const Logo = styled.h1`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${props => props.theme.colors.text.inverse};
`;

const SidebarNav = styled.nav`
  padding: ${props => props.theme.spacing.lg} 0;
  flex: 1;
  overflow-y: auto;
`;

const NavGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const GroupTitle = styled.h3`
  margin: 0 0 ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
`;

const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
  border-left: 3px solid transparent;
  margin: 2px ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-weight: 500;
  font-size: 0.95rem;

  &:hover {
    color: ${props => props.theme.colors.text.inverse};
    background: rgba(255, 255, 255, 0.08);
    transform: translateX(2px);
  }

  &.active {
    background: rgba(255, 255, 255, 0.14);
    border-left-color: ${props => props.theme.colors.secondary};
    color: ${props => props.theme.colors.text.inverse};
    font-weight: 600;
  }

  svg {
    width: 19px;
    height: 19px;
    margin-right: ${props => props.theme.spacing.md};
    opacity: 0.85;
    flex-shrink: 0;
  }
`;

const MenuToggleButton = styled.button`
  display: none;
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1001;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: none;
  padding: 10px;
  border-radius: ${props => props.theme.borderRadius.medium};
  cursor: pointer;
  box-shadow: ${props => props.theme.shadows.medium};

  @media (max-width: 768px) {
    display: block;
  }
`;

const UserInfo = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: rgba(0, 0, 0, 0.12);
  border-top: 1px solid rgba(255, 255, 255, 0.14);
`;

const UserName = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  color: ${props => props.theme.colors.text.inverse};
`;

const UserRole = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.65);
  text-transform: capitalize;
`;

const LogoutButton = styled.button`
  width: 100%;
  margin-top: ${props => props.theme.spacing.md};
  background: rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.colors.text.inverse};
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.medium};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: ${props => props.theme.fonts.main};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.35);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const Sidebar = ({ isOpen, onToggle }) => {
  const { user, logout, hasRole } = useAuth();

  return (
    <>
      <MenuToggleButton onClick={onToggle}>
        <FiMenu />
      </MenuToggleButton>

      <SidebarContainer $isOpen={isOpen}>
        <SidebarHeader>
          <Logo>Employee Calendar</Logo>
        </SidebarHeader>

        <SidebarNav>
          <NavGroup>
            <GroupTitle>Main</GroupTitle>
            <StyledNavLink to="/" end>
              <FiCalendar />
              Calendar
            </StyledNavLink>
          </NavGroup>

          {hasRole(['admin', 'store_manager','manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']) && (
            <NavGroup>
              <GroupTitle>Management</GroupTitle>
              <StyledNavLink to="/branches">
                <FiMapPin />
                Branch Management
              </StyledNavLink>
              <StyledNavLink to="/bird-eye-view">
                <FiEye />
                Bird's Eye View
              </StyledNavLink>
              <StyledNavLink to="/multi-branch">
                <FiLayers />
                Multi-Branch Management
              </StyledNavLink>
              <StyledNavLink to="/employees">
                <FiUsers />
                Users Management
              </StyledNavLink>
              <StyledNavLink to="/holidays">
                <FiGift />
                Public Holidays
              </StyledNavLink>
              {hasRole(['admin', 'store_manager','manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']) && (
                <StyledNavLink to="/leave-management">
                  <FiUser />
                  Leave Management
                </StyledNavLink>
              )}
              {hasRole(['admin', 'store_manager','manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']) && (
                <StyledNavLink to="/audit-logs">
                  <FiFileText />
                  Audit Logs
                </StyledNavLink>
              )}
            </NavGroup>
          )}

          {hasRole(['admin', 'store_manager','manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']) && (
            <NavGroup>
              <GroupTitle>Reports</GroupTitle>
              <StyledNavLink to="/summary">
                <FiBarChart />
                Summary
              </StyledNavLink>
            </NavGroup>
          )}
        </SidebarNav>

        <UserInfo>
          <UserName>{user?.name || user?.username}</UserName>
          <UserRole>{user?.role}</UserRole>
          <LogoutButton onClick={logout}>
            <FiLogOut style={{ marginRight: '8px' }} />
            Logout
          </LogoutButton>
        </UserInfo>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
